import { FC } from 'react';

import { HStack, IconButton, Modal, ModalBody, ModalContent, ModalHeader, ModalOverlay, Text, XOutline } from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { useActivityLinks } from '../../hooks/use-activity-links';
import { useGoal } from '../../hooks/use-goals';
import { AllActivityView } from '../activity-cards';

interface AddActivityToGoalModalProps {
	isOpen: boolean;
	onClose: () => void;
	goalId: string;
}

export const AddActivityToGoalModal: FC<AddActivityToGoalModalProps> = ({ isOpen, onClose, goalId }) => {
	const user = useCurrentUser();
	const { link, unlink, mapToBackendActivityType, isActivityLoading } = useActivityLinks();
	const { data: goal } = useGoal(goalId, { enabled: isOpen });

	// Check if an activity is linked to this goal
	const isLinked = (activityType: 'bonus' | 'feedback', activityId: string): boolean => {
		return goal?.activities?.some((activity) => activity.type === mapToBackendActivityType(activityType) && activity.id === activityId) ?? false;
	};

	const linkToGoal = (activityType: 'bonus' | 'feedback', activityId: string) => {
		link({ entityType: 'goal', entityId: goalId, activityType, activityId });
	};

	const unlinkFromGoal = (activityType: 'bonus' | 'feedback', activityId: string) => {
		unlink({ entityType: 'goal', entityId: goalId, activityType, activityId });
	};

	if (!user?.id) {
		return null;
	}

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="xl">
			<ModalOverlay />
			<ModalContent maxH={{ base: '90vh', md: '80vh' }} display="flex" flexDirection="column" bg="linear-gradient(345.75deg, #F6F6F9 66.48%, #FFFFFF 95.93%)">
				<ModalHeader pb={3}>
					<HStack justify="space-between" align="center">
						<Text fontSize="xl" fontWeight="semibold">
							Add Activity to Goal
						</Text>
						<IconButton aria-label="Close modal" icon={<XOutline />} variant="ghost" size="sm" onClick={onClose} />
					</HStack>
				</ModalHeader>
				<ModalBody px={5} py={4} flex={1} display="flex" flexDirection="column" overflow="hidden">
					<AllActivityView
						targetUserId={user.id}
						viewerId={user.id}
						onClose={onClose}
						showLinkButtons={true}
						isLinked={isLinked}
						isLinkLoading={isActivityLoading}
						onLink={(activityType, activityId) => {
							linkToGoal(activityType, activityId);
						}}
						onUnlink={(activityType, activityId) => {
							unlinkFromGoal(activityType, activityId);
						}}
					/>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
};
